import { useState, useRef, useEffect, useCallback } from 'react';
import { useEditor as useTiptapEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import CodeBlock from '@tiptap/extension-code-block';
import Blockquote from '@tiptap/extension-blockquote';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import { formatDateForInput, formatDateForDisplay } from '../utils/dateUtils';
import { checkGrammar } from '../utils/grammarChecker';

export const useEditor = (savePost, user, categories = []) => {
  const [isComposing, setIsComposing] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [composeTitle, setComposeTitle] = useState('');
  const [composeCategory, setComposeCategory] = useState(categories[0] || 'General');
  const [composeDate, setComposeDate] = useState('');
  const [saveStatus, setSaveStatus] = useState('idle');
  const [liveStats, setLiveStats] = useState({ words: 0, time: 0 });
  const [localDraftAvailable, setLocalDraftAvailable] = useState(false);
  const [grammarIssues, setGrammarIssues] = useState([]);
  const [isCheckingGrammar, setIsCheckingGrammar] = useState(false);

  const lastSavedRef = useRef({ title: '', content: '', category: '' });
  const debounceTimerRef = useRef(null);
  const handleSavePostRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());

  // Stable refs to prevent stale closure bugs in Tiptap's callbacks
  const editingPostIdRef = useRef(editingPostId);
  const composeTitleRef = useRef(composeTitle);
  const composeCategoryRef = useRef(composeCategory);
  const composeDateRef = useRef(composeDate);
  const isComposingRef = useRef(isComposing);

  useEffect(() => {
    editingPostIdRef.current = editingPostId;
    composeTitleRef.current = composeTitle;
    composeCategoryRef.current = composeCategory;
    composeDateRef.current = composeDate;
    isComposingRef.current = isComposing;
  }, [editingPostId, composeTitle, composeCategory, composeDate, isComposing]);

  // Check if a local draft is available on mount or editor open
  useEffect(() => {
    const draft = localStorage.getItem('idiawisdom_local_draft');
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        // Only show draft if it actually has content or title
        const hasData = parsed.title?.trim() || (parsed.content && parsed.content !== '<p></p>');
        setLocalDraftAvailable(!!hasData);
      } catch (e) {
        setLocalDraftAvailable(false);
      }
    } else {
      setLocalDraftAvailable(false);
    }
  }, [isComposing]);

  // --- Tiptap Engine Initialization ---
  const editor = useTiptapEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      CodeBlock,
      Blockquote,
      HorizontalRule,
      Placeholder.configure({
        placeholder: 'Begin your reflection...',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[60vh] focus:outline-none prose prose-stone dark:prose-invert max-w-none',
        spellcheck: 'false',
      },
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText() || "";
      const words = text.trim() ? text.trim().split(/\s+/).length : 0;
      setLiveStats({ words, time: Math.max(1, Math.ceil(words / 200)) });
      
      // Save local draft to localStorage in real-time
      if (isComposingRef.current) {
        const content = editor.getHTML();
        if (composeTitleRef.current.trim() || (content && content !== '<p></p>')) {
          localStorage.setItem('idiawisdom_local_draft', JSON.stringify({
            id: editingPostIdRef.current,
            title: composeTitleRef.current,
            content: content,
            category: composeCategoryRef.current,
            date: composeDateRef.current,
            timestamp: Date.now()
          }));
          setLocalDraftAvailable(true);
        }
      }

      triggerSmartSync();
    },
  });

  // Save to localStorage when header details change
  useEffect(() => {
    if (isComposing && editor) {
      const content = editor.getHTML();
      if (composeTitle.trim() || (content && content !== '<p></p>')) {
        localStorage.setItem('idiawisdom_local_draft', JSON.stringify({
          id: editingPostId,
          title: composeTitle,
          content: content,
          category: composeCategory,
          date: composeDate,
          timestamp: Date.now()
        }));
        setLocalDraftAvailable(true);
      }
    }
  }, [composeTitle, composeCategory, composeDate, isComposing, editor, editingPostId]);

  // Keep stable ref for Firebase autosave
  useEffect(() => {
    handleSavePostRef.current = handleSavePost;
  });

  // --- Autosave Trigger ---
  const triggerSmartSync = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (handleSavePostRef.current) {
        handleSavePostRef.current('draft', true);
      }
    }, 2000);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const executeCommand = (command) => {
    if (!editor) return;
    const chain = editor.chain().focus();
    switch (command) {
      case 'bold': chain.toggleBold().run(); break;
      case 'italic': chain.toggleItalic().run(); break;
      case 'underline': chain.toggleUnderline().run(); break;
      case 'strike': chain.toggleStrike().run(); break;
      case 'h1': chain.toggleHeading({ level: 1 }).run(); break;
      case 'h2': chain.toggleHeading({ level: 2 }).run(); break;
      case 'h3': chain.toggleHeading({ level: 3 }).run(); break;
      case 'bullet': chain.toggleBulletList().run(); break;
      case 'number': chain.toggleOrderedList().run(); break;
      case 'blockquote': chain.toggleBlockquote().run(); break;
      case 'codeBlock': chain.toggleCodeBlock().run(); break;
      case 'divider': chain.setHorizontalRule().run(); break;
      case 'emDash': chain.insertContent('—').run(); break;
      case 'undo': chain.undo().run(); break;
      case 'redo': chain.redo().run(); break;
      default: break;
    }
  };

  const handleGrammarCheck = useCallback(async () => {
    if (!editor || isCheckingGrammar) return;
    const text = editor.getText();
    if (!text.trim()) return;

    setIsCheckingGrammar(true);
    setGrammarIssues([]);

    try {
      const results = await checkGrammar(text);
      setGrammarIssues(results);
    } catch (e) {
      console.error('Grammar check error:', e);
    } finally {
      setIsCheckingGrammar(false);
    }
  }, [editor, isCheckingGrammar]);

  const replaceGrammarFix = useCallback((error) => {
    if (!editor) return;
    const { doc } = editor.state;
    const fullText = doc.textBetween(0, doc.content.size, '\n', false);
    const idx = fullText.indexOf(error.word);
    if (idx === -1) return;

    editor
      .chain()
      .focus()
      .setTextSelection({ from: idx, to: idx + error.word.length })
      .deleteSelection()
      .insertContent(error.suggestion)
      .run();

    // Remove this issue from the list
    setGrammarIssues(prev => prev.filter(i => i.word !== error.word));
  }, [editor]);

  const clearGrammarIssues = useCallback(() => {
    setGrammarIssues([]);
    setIsCheckingGrammar(false);
  }, []);

  const ignoreGrammarIssue = useCallback((index) => {
    setGrammarIssues(prev => prev.filter((_, i) => i !== index));
  }, []);

  const openNewPostEditor = () => {
    setEditingPostId(null);
    setComposeTitle('');
    setComposeCategory(categories[0] || 'General');
    setComposeDate(formatDateForInput(new Date()));
    setIsComposing(true);
    setSaveStatus('idle');
    lastSavedRef.current = { 
      title: '', 
      content: '', 
      category: categories[0] || 'General' 
    };
    if (editor) editor.commands.setContent('');
  };

  const openEditPostEditor = (post) => {
    setEditingPostId(post.id);
    setComposeTitle(post.title);
    setComposeCategory(post.category || categories[0] || 'General');
    setComposeDate(formatDateForInput(post.date));
    setIsComposing(true);
    if (editor) {
      editor.commands.setContent(post.content || '');
      lastSavedRef.current = { 
        title: post.title, 
        content: post.content || '', 
        category: post.category || categories[0] || 'General' 
      };
      const text = editor.getText() || "";
      setLiveStats({ words: text.trim() ? text.trim().split(/\s+/).length : 0, time: Math.max(1, Math.ceil(text.length / 200)) });
    }
  };

  const restoreLocalDraft = () => {
    const draft = localStorage.getItem('idiawisdom_local_draft');
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        setEditingPostId(parsed.id || null);
        setComposeTitle(parsed.title || '');
        setComposeCategory(parsed.category || categories[0] || 'General');
        setComposeDate(parsed.date || formatDateForInput(new Date()));
        if (editor) {
          editor.commands.setContent(parsed.content || '');
          const text = editor.getText() || "";
          setLiveStats({ words: text.trim() ? text.trim().split(/\s+/).length : 0, time: Math.max(1, Math.ceil(text.length / 200)) });
        }
        setLocalDraftAvailable(false);
      } catch (e) {
        console.error("Failed to restore local draft:", e);
      }
    }
  };

  const clearLocalDraft = () => {
    localStorage.removeItem('idiawisdom_local_draft');
    setLocalDraftAvailable(false);
  };

  const closeEditor = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setIsComposing(false);
    setEditingPostId(null);
    clearLocalDraft();
  };

  const handleSavePost = async (statusToSave, isAutosave = false) => {
    if (!user || !editor) return;

    const doSave = async () => {
      if (!isAutosave && debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      const currentTitle = composeTitle.trim();
      const content = editor.getHTML();
      const textOnly = editor.getText().trim();

      if (isAutosave && !currentTitle && !textOnly) return;
      if (isAutosave && 
          lastSavedRef.current.title === currentTitle &&
          lastSavedRef.current.content === content &&
          lastSavedRef.current.category === composeCategory) {
        return;
      }

      setSaveStatus('saving');
      const wordCount = textOnly ? textOnly.split(/\s+/).length : 0;
      const postData = {
        title: currentTitle || 'Untitled Draft',
        content: content,
        excerpt: textOnly.substring(0, 180) + (textOnly.length > 180 ? '...' : ''),
        category: composeCategory,
        readTime: Math.max(1, Math.ceil(wordCount / 200)) + " min read",
        date: formatDateForDisplay(composeDate),
        status: statusToSave,
        updatedAt: Date.now()
      };

      try {
        const result = await savePost(postData, editingPostIdRef.current);
        if (result.success) {
          lastSavedRef.current = { title: currentTitle, content: content, category: composeCategory };
          setSaveStatus('success');
          clearLocalDraft(); // Success! Clear the recovery buffer
          if (!isAutosave && statusToSave === 'published') closeEditor();
          else if (!editingPostIdRef.current && result.id) {
            setEditingPostId(result.id);
            editingPostIdRef.current = result.id;
          }
        }
      } catch (e) { console.error(e); }
      setTimeout(() => setSaveStatus('idle'), 3000);
    };

    const prev = saveQueueRef.current;
    saveQueueRef.current = prev.then(doSave, doSave);
    return saveQueueRef.current;
  };

  return {
    isComposing, editingPostId, composeTitle, setComposeTitle,
    composeCategory, setComposeCategory, composeDate, setComposeDate,
    saveStatus, editor, executeCommand, openNewPostEditor,
    openEditPostEditor, closeEditor, handleSavePost, liveStats, triggerSmartSync,
    localDraftAvailable, restoreLocalDraft, clearLocalDraft,
    grammarIssues, isCheckingGrammar,
    handleGrammarCheck, replaceGrammarFix, clearGrammarIssues,
    ignoreGrammarIssue
  };
};